// Retention facts the admin feedback page needs (#14561; docs/compliance/support-feedback-data-retention.md).
// This app must not import modules/support, so these are small copies. Each is tied to its source by a test:
// apps/support-attachment-av-scan/test/retention-lifecycle-contract.test.ts fails when the retention days
// drift from the bucket's lifecycle rule and the module constant, and the same test fails when the
// reference pattern here or in the admin feedback router drifts from the module's schema.

/** Screenshots are deleted this many days after upload; the module refuses a signed URL from that age. */
export const ATTACHMENT_RETENTION_DAYS = 90;

const DAY_MS = 86_400_000;

/**
 * True once an attachment has reached its retention age (the boundary counts: the bucket's lifecycle rule
 * may already have run). Same rule as modules/support/domain/attachment-retention.ts, so the page stops
 * offering a button the server would answer with 410.
 */
export function isAttachmentExpired(createdAtIso: string, nowMs: number = Date.now()): boolean {
  const createdMs = Date.parse(createdAtIso);
  if (Number.isNaN(createdMs)) return false;
  return nowMs - createdMs >= ATTACHMENT_RETENTION_DAYS * DAY_MS;
}

/**
 * The privacy-request log reference an erase must carry: an id such as PR-2026-014, never a name or email,
 * because it is written to the append-only audit log. Keep equal to REQUEST_REF_PATTERN in
 * modules/support/schemas/index.ts and to REQUEST_REF in the admin feedback router.
 */
export const REQUEST_REF_PATTERN = /^(?=.*[0-9])[A-Za-z0-9._-]{3,64}$/;

export const REQUEST_REF_HELP =
  'Use the reference from the privacy-request log, such as PR-2026-014: letters, digits, dot, dash or underscore, 3 to 64 characters, with at least one digit. Never a name or an email.';

export const ERASE_STORAGE_INCOMPLETE_TEXT =
  'Erasure incomplete: a screenshot could not be deleted, so the request was kept. Screenshots without a hold may already be deleted. Check for a legal hold or a storage fault, then run it again; it is safe to repeat.';

export const ERASE_NOT_FOUND_TEXT =
  'This request no longer exists: it was already erased, possibly by an earlier attempt whose answer was lost, or by someone else. Return to the inbox; if it is not listed, the erasure ran.';

export const ERASE_UNAVAILABLE_TEXT =
  'The feedback service is temporarily unavailable. The erasure may not have run; try again, it is safe to repeat.';
